import { Link, useLocation } from 'react-router-dom';
import { LayoutDashboard, Bike, RefreshCw, Wrench, Users, User, MoreHorizontal, X, LogOut, Battery, Building2, AlertTriangle, Mail, DollarSign, Wallet, UserPlus, Package, Shield, FileText, Download, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';

/**
 * Bottom Navigation for mobile devices
 * Shows 5 main items + "More" menu for additional navigation
 */
const BottomNavigation = ({ user, menuItems = [], adminMenuItems = [], onLogout }) => {
  const location = useLocation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [adminExpanded, setAdminExpanded] = useState(false);

  // Helper: does the user have permission to view this module key?
  // (mirrors the rule used by the side Layout)
  const canViewModule = (moduleKey) => {
    if (!moduleKey) return true;
    if (user?.role === 'Super Admin' || user?.role === 'Admin') return true;
    return user?.permissions?.[moduleKey]?.view === true;
  };

  // Primary navigation items for bottom bar (most used on mobile)
  // Each item has a `moduleKey` so we can filter by user permission.
  const allPrimaryNavItems = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard', moduleKey: 'dashboard' },
    { path: '/motorcycles', icon: Bike, label: 'Motorcycles', moduleKey: 'motorcycles' },
    { path: '/battery-swaps', icon: RefreshCw, label: 'Swaps', moduleKey: 'batterySwaps' },
    { path: '/maintenance', icon: Wrench, label: 'Maintenance', moduleKey: 'maintenance' },
    { path: '/riders', icon: Users, label: 'Riders', moduleKey: 'riders' },
  ];

  // Only keep primary items the user has permission to view. If the user has
  // permission for fewer than 5 primary items, fill the remaining slots from
  // the (already-permission-filtered) `menuItems` so the bottom bar always
  // surfaces pages relevant to *this* user.
  const allowedPrimary = allPrimaryNavItems.filter(item => canViewModule(item.moduleKey));
  const primaryNavItems = [
    ...allowedPrimary,
    ...menuItems
      .filter(m => !allowedPrimary.some(p => p.path === m.path) && m.path !== '/profile')
      .slice(0, Math.max(0, 5 - allowedPrimary.length)),
  ].slice(0, 5);

  // Filter menu items for "More" menu (excluding what's already in the bottom bar)
  const moreMenuItems = menuItems.filter(
    item => !primaryNavItems.some(primary => primary.path === item.path)
  );

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav 
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 50,
          backgroundColor: 'white',
          borderTop: '1px solid #e2e8f0',
          boxShadow: '0 -4px 6px -1px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div className="flex items-center justify-around h-16 px-1">
          {primaryNavItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full px-1 transition-all ${
                  active
                    ? 'text-green-600'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              >
                <div className={`p-1.5 rounded-xl transition-all ${
                  active ? 'bg-green-100' : ''
                }`}>
                  <Icon className={`w-5 h-5 ${active ? 'stroke-[2.5]' : ''}`} />
                </div>
                <span className={`text-[10px] mt-0.5 font-medium ${
                  active ? 'text-green-700' : ''
                }`}>
                  {item.label}
                </span>
              </Link>
            );
          })}

          {/* More Menu Button */}
          <button
            onClick={() => setMoreMenuOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full px-1 text-slate-500 hover:text-slate-700 transition-all"
            data-testid="bottom-nav-more"
          >
            <div className="p-1.5 rounded-xl">
              <MoreHorizontal className="w-5 h-5" />
            </div>
            <span className="text-[10px] mt-0.5 font-medium">More</span>
          </button>
        </div>
      </nav>

      {/* More Menu Dialog */}
      <Dialog open={moreMenuOpen} onOpenChange={setMoreMenuOpen}>
        <DialogContent className="w-[95vw] max-w-md mx-auto rounded-2xl max-h-[85vh] overflow-hidden p-0">
          <DialogHeader className="p-4 border-b bg-slate-50">
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-lime-500 to-green-600 rounded-full flex items-center justify-center text-white font-semibold overflow-hidden">
                {user?.profile_photo ? (
                  <img src={user.profile_photo} alt={user.name} className="w-full h-full object-cover" />
                ) : (
                  user?.name?.charAt(0) ? user.name.charAt(0) : <User className="w-5 h-5" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-slate-800">{user?.name || 'User'}</p>
                <p className="text-xs text-slate-500 font-normal">{user?.email}</p>
              </div>
            </DialogTitle>
          </DialogHeader>
          
          <div className="p-4 overflow-y-auto max-h-[60vh]">
            <div className="grid grid-cols-3 gap-3">
              {/* Profile Link */}
              <Link
                to="/profile"
                onClick={() => setMoreMenuOpen(false)}
                className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all ${
                  isActive('/profile')
                    ? 'bg-green-100 text-green-700'
                    : 'bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                }`}
              >
                <User className="w-6 h-6 mb-2" />
                <span className="text-xs font-medium text-center">My Profile</span>
              </Link>

              {moreMenuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreMenuOpen(false)}
                    className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all ${
                      active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                    }`}
                  >
                    <Icon className="w-6 h-6 mb-2" />
                    <span className="text-xs font-medium text-center line-clamp-2">{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Admin Section */}
            {adminMenuItems.length > 0 && (
              <div className="mt-4 pt-4 border-t border-slate-200">
                <button
                  onClick={() => setAdminExpanded(!adminExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-red-50 rounded-xl text-red-700 font-medium"
                >
                  <div className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    <span>Admin</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-red-100 px-2 py-0.5 rounded-full">
                      {user?.role}
                    </span>
                    {adminExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </div>
                </button>
                
                {adminExpanded && (
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    {adminMenuItems.map((item) => {
                      const Icon = item.icon;
                      const currentFullPath = location.pathname + location.search;
                      const active = currentFullPath === item.path || location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setMoreMenuOpen(false)}
                          className={`flex flex-col items-center justify-center p-4 rounded-2xl transition-all ${
                            active
                              ? 'bg-red-100 text-red-700'
                              : 'bg-slate-50 text-slate-600 hover:bg-slate-100 active:bg-slate-200'
                          }`}
                        >
                          <Icon className="w-6 h-6 mb-2" />
                          <span className="text-xs font-medium text-center line-clamp-2">{item.label}</span>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Logout Button */}
          <div className="p-4 bg-slate-50 border-t">
            <Button
              onClick={() => {
                setMoreMenuOpen(false);
                onLogout?.();
              }}
              variant="outline"
              className="w-full py-3 text-red-600 border-red-200 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BottomNavigation;
